import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;


// Convert any axios error / Pydantic validation array to a clean string
export function apiErrMsg(err, fallback = 'Request failed') {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  if (d && typeof d === 'object' && d.msg) return d.msg;
  return err?.message || fallback;
}

export const api = axios.create({ baseURL: API, timeout: 15000 });

export function setAuthToken(token) {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('alveetrade_token', token);
  } else {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('alveetrade_token');
  }
}

const savedToken = typeof window !== 'undefined' ? localStorage.getItem('alveetrade_token') : null;
if (savedToken) api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;

export async function getGeo() {
  const { data } = await api.get('/geo');
  return data;
}

export async function getPublicSettings() {
  const { data } = await api.get('/settings');
  return data;
}

function _collectAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || null,
      utm_campaign: params.get('utm_campaign') || null,
      utm_medium: params.get('utm_medium') || null,
      referrer: (typeof document !== 'undefined' && document.referrer) || null,
      adsterra_subid: getAdsterraSubid(),
      fbclid: getFbclid(),
    };
  } catch {
    return {};
  }
}

// ---- Facebook click id (fbclid) capture ----
// When a user clicks an FB ad, FB appends ?fbclid=<token> to the destination URL.
// We persist it so subsequent events (tg_click, broker_click) can be attributed
// back to Facebook ad traffic regardless of referrer leakage.
const FBCLID_KEY = 'alveetrade_fbclid';
const FBCLID_TTL_DAYS = 30;

export function captureFbclidFromUrl() {
  try {
    if (typeof window === 'undefined') return null;
    const sp = new URLSearchParams(window.location.search);
    const val = sp.get('fbclid');
    if (!val) return getFbclid();
    const payload = { v: val, t: Date.now() };
    try { localStorage.setItem(FBCLID_KEY, JSON.stringify(payload)); } catch { /* noop */ }
    return val;
  } catch {
    return null;
  }
}

export function getFbclid() {
  try {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(FBCLID_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.v) return null;
    const ageDays = (Date.now() - (obj.t || 0)) / (1000 * 60 * 60 * 24);
    if (ageDays > FBCLID_TTL_DAYS) {
      localStorage.removeItem(FBCLID_KEY);
      return null;
    }
    return obj.v;
  } catch {
    return null;
  }
}

// ---- Adsterra subid capture ----
// We persist the subid the visitor arrived with so subsequent events
// (Telegram clicks, broker redirects, broker-side conversions via PO postback)
// can be attributed back to the original Adsterra click.
const ADSTERRA_KEY = 'alveetrade_adsterra_subid';
const ADSTERRA_TTL_DAYS = 30;
// Common parameter names Adsterra (and similar networks) use for subid
const ADSTERRA_PARAM_NAMES = ['aff_sub', 'subid', 'cnv_id', 'click_id', 'clickid'];

function _computeDeviceFingerprint() {
  // Stable, lightweight fingerprint built from properties that don't change between
  // sessions on the same device + browser. Hashed server-side; we only send the raw
  // string. NOT a tracking cookie — purely used for CPA fraud dedupe.
  try {
    if (typeof window === 'undefined') return '';
    const parts = [
      navigator.userAgent || '',
      navigator.language || '',
      (navigator.languages || []).join(',') || '',
      navigator.platform || '',
      String(navigator.hardwareConcurrency || ''),
      String(navigator.maxTouchPoints || ''),
      String(window.screen?.width || '') + 'x' + String(window.screen?.height || ''),
      String(window.screen?.colorDepth || ''),
      String(window.devicePixelRatio || ''),
      Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      String(new Date().getTimezoneOffset()),
    ];
    return parts.join('|');
  } catch {
    return '';
  }
}

export function captureAdsterraSubidFromUrl() {
  try {
    if (typeof window === 'undefined') return null;
    const sp = new URLSearchParams(window.location.search);
    let val = null;
    for (const k of ADSTERRA_PARAM_NAMES) {
      const v = sp.get(k);
      if (v) { val = v; break; }
    }
    if (!val) return getAdsterraSubid();
    const payload = { v: val, t: Date.now() };
    try { localStorage.setItem(ADSTERRA_KEY, JSON.stringify(payload)); } catch { /* noop */ }
    // Register the landing on backend so fraud rules can later check IP intel.
    // Fire-and-forget — never block the page.
    try {
      api.post('/cpa/click', {
        subid: val,
        source: 'landing',
        fingerprint: _computeDeviceFingerprint(),
      }).catch(() => {});
    } catch { /* noop */ }
    return val;
  } catch {
    return null;
  }
}

export function getAdsterraSubid() {
  try {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(ADSTERRA_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.v) return null;
    const ageDays = (Date.now() - (obj.t || 0)) / (1000 * 60 * 60 * 24);
    if (ageDays > ADSTERRA_TTL_DAYS) {
      localStorage.removeItem(ADSTERRA_KEY);
      return null;
    }
    return obj.v;
  } catch {
    return null;
  }
}

export async function track(event) {
  const attribution = _collectAttribution();
  const payload = { ...attribution, ...event };
  try { await api.post('/track', payload); } catch (e) { /* swallow */ }
}

export async function getPublicStats() {
  const { data } = await api.get('/public-stats');
  return data;
}

// Admin
export async function adminLogin(email, password) {
  const { data } = await api.post('/admin/login', { email, password });
  setAuthToken(data.token);
  return data;
}

export async function adminMe() {
  const { data } = await api.get('/admin/me');
  return data;
}

export async function adminGetSettings() {
  const { data } = await api.get('/admin/settings');
  return data;
}

export async function adminUpdateSettings(payload) {
  const { data } = await api.put('/admin/settings', payload);
  return data;
}

export async function adminSummary(params = {}) {
  const { data } = await api.get('/admin/analytics/summary', { params });
  return data;
}

export async function adminByCountry(params = {}) {
  const { data } = await api.get('/admin/analytics/by-country', { params });
  return data;
}

export async function adminByPlatform(params = {}) {
  const { data } = await api.get('/admin/analytics/by-platform', { params });
  return data;
}

export async function adminTimeseries(params = {}) {
  // Back-compat: allow a plain number (days)
  const q = typeof params === 'number' ? { days: params } : params;
  const { data } = await api.get('/admin/analytics/timeseries', { params: q });
  return data;
}

export async function adminBrokerAnalytics(params = {}) {
  const { data } = await api.get('/admin/analytics/broker', { params });
  return data;
}

export async function adminPostbackInfo() {
  const { data } = await api.get('/admin/postback/info');
  return data;
}

export async function adminShortlinksList() {
  const { data } = await api.get('/admin/shortlinks');
  return data;
}
export async function adminShortlinkCreate(url, note, slug) {
  const payload = { url };
  if (note) payload.note = note;
  if (slug) payload.slug = slug;
  const { data } = await api.post('/admin/shortlinks', payload);
  return data;
}
export async function adminShortlinkCheckSlug(slug) {
  const { data } = await api.get(`/admin/shortlinks/check/${encodeURIComponent(slug)}`);
  return data;
}
export async function adminShortlinkDelete(slug) {
  const { data } = await api.delete(`/admin/shortlinks/${encodeURIComponent(slug)}`);
  return data;
}
export async function adminShortlinkClicks(slug) {
  const { data } = await api.get(`/admin/shortlinks/${encodeURIComponent(slug)}/clicks`);
  return data;
}

// ---- Ads Hub ----
export async function adminAdCopyGenerate(payload) {
  const { data } = await api.post('/admin/ad-copy/generate', payload);
  return data;
}
export async function adminAdCopyHistory(limit = 20) {
  const { data } = await api.get('/admin/ad-copy/history', { params: { limit } });
  return data;
}
export async function adminCampaignBuild(payload) {
  const { data } = await api.post('/admin/campaigns/build', payload);
  return data;
}
export async function adminCapiTest() {
  const { data } = await api.post('/admin/capi/test');
  return data;
}
export async function adminCapiLog(limit = 30) {
  const { data } = await api.get('/admin/capi/log', { params: { limit } });
  return data;
}

// ---- Blog (admin) ----
export async function adminBlogList(status = null) {
  const { data } = await api.get('/admin/blog/posts', { params: status ? { status_filter: status } : {} });
  return data;
}
export async function adminBlogCreate(payload) {
  const { data } = await api.post('/admin/blog/posts', payload);
  return data;
}
export async function adminBlogGet(id) {
  const { data } = await api.get(`/admin/blog/posts/${encodeURIComponent(id)}`);
  return data;
}
export async function adminBlogUpdate(id, payload) {
  const { data } = await api.put(`/admin/blog/posts/${encodeURIComponent(id)}`, payload);
  return data;
}
export async function adminBlogDelete(id) {
  const { data } = await api.delete(`/admin/blog/posts/${encodeURIComponent(id)}`);
  return data;
}
export async function adminBlogPublishToggle(id) {
  const { data } = await api.post(`/admin/blog/posts/${encodeURIComponent(id)}/publish`);
  return data;
}
export async function adminBlogAiGenerate(payload) {
  // Long blog generation can take 60-180s; give it plenty of headroom.
  const { data } = await api.post('/admin/blog/ai-generate', payload, { timeout: 240000 });
  return data;
}
export async function adminBlogRenderPreview(content_md) {
  const { data } = await api.post('/admin/blog/render-preview', { content_md });
  return data;
}
export async function adminBlogCategories() {
  const { data } = await api.get('/admin/blog/categories');
  return data;
}
export async function adminUploadBlogImage(file) {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post('/admin/uploads/blog-image', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  return data;
}

// ---- Blog (public) ----
export async function publicBlogList(params = {}) {
  const { data } = await api.get('/blog/posts', { params });
  return data;
}
export async function publicBlogPost(slug) {
  const { data } = await api.get(`/blog/posts/${encodeURIComponent(slug)}`);
  return data;
}
export async function publicBlogCategories() {
  const { data } = await api.get('/blog/categories');
  return data;
}

// ---- Student Wins ----
export async function publicStudentWins() {
  const { data } = await api.get('/student-wins');
  return data;
}
export async function adminStudentWinsList() {
  const { data } = await api.get('/admin/student-wins');
  return data;
}
export async function adminStudentWinsCreate(payload) {
  const { data } = await api.post('/admin/student-wins', payload);
  return data;
}
export async function adminStudentWinsUpdate(id, payload) {
  const { data } = await api.put(`/admin/student-wins/${encodeURIComponent(id)}`, payload);
  return data;
}
export async function adminStudentWinsDelete(id) {
  const { data } = await api.delete(`/admin/student-wins/${encodeURIComponent(id)}`);
  return data;
}
export async function adminStudentWinsReorder(ids) {
  const { data } = await api.post('/admin/student-wins/reorder', { ids });
  return data;
}
export async function adminUploadStudentWin(file) {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post('/admin/uploads/student-win', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  return data;
}

// ---- Elite Network (lifestyle/network photos) ----
export async function publicEliteNetwork() {
  const { data } = await api.get('/elite-network');
  return data;
}
export async function adminEliteNetworkList() {
  const { data } = await api.get('/admin/elite-network');
  return data;
}
export async function adminEliteNetworkCreate(payload) {
  const { data } = await api.post('/admin/elite-network', payload);
  return data;
}
export async function adminEliteNetworkUpdate(id, payload) {
  const { data } = await api.put(`/admin/elite-network/${encodeURIComponent(id)}`, payload);
  return data;
}
export async function adminEliteNetworkDelete(id) {
  const { data } = await api.delete(`/admin/elite-network/${encodeURIComponent(id)}`);
  return data;
}
export async function adminEliteNetworkReorder(ids) {
  const { data } = await api.post('/admin/elite-network/reorder', { ids });
  return data;
}
export async function adminUploadEliteNetwork(file) {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post('/admin/uploads/elite-network', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  return data;
}

export function brokerRedirectUrl(source = 'broker_section', broker = 'po') {
  // Build full redirect URL with current page UTMs forwarded for accurate attribution.
  // broker = 'po' → /broker/redirect (Pocket Option default)
  // broker = 'quotex' → /broker/redirect/quotex
  const path = broker === 'quotex' ? '/broker/redirect/quotex' : '/broker/redirect';
  try {
    const sp = new URLSearchParams(window.location.search);
    const params = new URLSearchParams({ source });
    ['utm_source', 'utm_campaign', 'utm_medium'].forEach((k) => {
      const v = sp.get(k);
      if (v) params.set(k, v);
    });
    // Forward Adsterra subid so the click record stores it for postback
    const sub = getAdsterraSubid();
    if (sub) params.set('aff_sub', sub);
    return `${API}${path}?${params.toString()}`;
  } catch {
    return `${API}${path}?source=${encodeURIComponent(source)}`;
  }
}

// ---- Admin: CPA / Adsterra ----
export async function adminCpaLog(limit = 50) {
  const { data } = await api.get(`/admin/cpa/log?limit=${limit}`);
  return data;
}
export async function adminCpaTest(eventKey, subid) {
  const { data } = await api.post('/admin/cpa/test', { event_key: eventKey, subid });
  return data;
}
export async function adminCpaClicks(limit = 80) {
  const { data } = await api.get(`/admin/cpa/clicks?limit=${limit}`);
  return data;
}

// ---- Giveaways (public + admin) ----
export async function publicGiveaways() {
  const { data } = await api.get('/giveaways');
  return data;
}
export async function publicGiveaway(slug) {
  const { data } = await api.get(`/giveaways/${encodeURIComponent(slug)}`);
  return data;
}
export async function publicGiveawayEnter(slug, payload) {
  const { data } = await api.post(`/giveaways/${encodeURIComponent(slug)}/enter`, payload);
  return data;
}
export async function publicGiveawayRecheck(slug, payload) {
  // Re-check eligibility for an existing entry without creating a new one
  const { data } = await api.post(`/giveaways/${encodeURIComponent(slug)}/recheck`, payload);
  return data;
}
export async function adminListGiveaways() {
  const { data } = await api.get('/admin/giveaways');
  return data;
}
export async function adminCreateGiveaway(payload) {
  const { data } = await api.post('/admin/giveaways', payload);
  return data;
}
export async function adminUpdateGiveaway(id, payload) {
  const { data } = await api.put(`/admin/giveaways/${encodeURIComponent(id)}`, payload);
  return data;
}
export async function adminDeleteGiveaway(id) {
  const { data } = await api.delete(`/admin/giveaways/${encodeURIComponent(id)}`);
  return data;
}
export async function adminGiveawayEntries(id, limit = 500) {
  const { data } = await api.get(`/admin/giveaways/${encodeURIComponent(id)}/entries?limit=${limit}`);
  return data;
}
export async function adminPickWinner(id) {
  const { data } = await api.post(`/admin/giveaways/${encodeURIComponent(id)}/pick-winner`);
  return data;
}
export async function adminClearWinner(id) {
  const { data } = await api.post(`/admin/giveaways/${encodeURIComponent(id)}/clear-winner`);
  return data;
}
export async function adminDeleteEntry(giveawayId, entryId) {
  const { data } = await api.delete(`/admin/giveaways/${encodeURIComponent(giveawayId)}/entries/${encodeURIComponent(entryId)}`);
  return data;
}
export async function adminUploadGiveawayCover(file) {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post('/admin/uploads/giveaway-cover', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  return data;
}

// ---- Quotex postback (broker) ----
export async function adminPostbackQuotexInfo() {
  const { data } = await api.get('/admin/postback/quotex/info');
  return data;
}

// ---- Bookings (1-on-1 calls) ----
export async function createBooking(body) {
  const { data } = await api.post('/bookings', body);
  return data;
}

export async function getBooking(id) {
  const { data } = await api.get(`/bookings/${id}`);
  return data;
}

export async function adminListBookings(limit = 100) {
  const { data } = await api.get('/admin/bookings', { params: { limit } });
  return data;
}

export async function adminUpdateBooking(id, patch) {
  const { data } = await api.patch(`/admin/bookings/${id}`, patch);
  return data;
}

export async function adminDeleteBooking(id) {
  const { data } = await api.delete(`/admin/bookings/${id}`);
  return data;
}


// ============================================================
//   CLIPPING PROGRAM
//   - Uses a SEPARATE axios instance + its own token store so
//     clipper auth never collides with admin auth.
// ============================================================
const CLIP_LS_TOKEN = 'alvee_clipper_token';

export const clipApi = axios.create({ baseURL: API, timeout: 15000 });

export function setClipperToken(token) {
  if (token) {
    clipApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem(CLIP_LS_TOKEN, token);
  } else {
    delete clipApi.defaults.headers.common['Authorization'];
    localStorage.removeItem(CLIP_LS_TOKEN);
  }
}

const _savedClipperToken = typeof window !== 'undefined' ? localStorage.getItem(CLIP_LS_TOKEN) : null;
if (_savedClipperToken) {
  clipApi.defaults.headers.common['Authorization'] = `Bearer ${_savedClipperToken}`;
}

export function getStoredClipperToken() {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(CLIP_LS_TOKEN);
}

export async function clipperSignup(payload) {
  const { data } = await clipApi.post('/clippers/signup', payload);
  if (data?.token) setClipperToken(data.token);
  return data;
}

export async function clipperLogin(payload) {
  const { data } = await clipApi.post('/clippers/login', payload);
  if (data?.token) setClipperToken(data.token);
  return data;
}

export async function clipperMe() {
  const { data } = await clipApi.get('/clippers/me');
  return data;
}

export async function clipperUpdateMe(patch) {
  const { data } = await clipApi.patch('/clippers/me', patch);
  return data;
}

export async function clipperListClips() {
  const { data } = await clipApi.get('/clippers/clips');
  return data;
}

export async function clipperSubmitClip(payload) {
  // Anti-fraud verification hits Apify server-side (owner + caption scrape),
  // which typically takes 20-90 seconds. Extend the client timeout accordingly.
  const { data } = await clipApi.post('/clippers/clips', payload, { timeout: 180000 });
  return data;
}

export async function clipperDeleteClip(id) {
  const { data } = await clipApi.delete(`/clippers/clips/${id}`);
  return data;
}

export async function clipperListWithdrawals() {
  const { data } = await clipApi.get('/clippers/withdrawals');
  return data;
}

export async function clipperRequestWithdrawal(payload) {
  const { data } = await clipApi.post('/clippers/withdrawals', payload);
  return data;
}

export async function clipperPublicStats() {
  const { data } = await clipApi.get('/clippers/public-stats');
  return data;
}

export async function clipperLeaderboard(limit = 5) {
  const { data } = await clipApi.get('/clippers/leaderboard', { params: { limit } });
  return data;
}

// ---------- Admin clip management (uses admin token via `api`) ----------
export async function adminListClippers(q = '', limit = 200) {
  const { data } = await api.get('/admin/clippers', { params: { q, limit } });
  return data;
}

export async function adminBlockClipper(clipperId, blocked = true) {
  const { data } = await api.post(`/admin/clippers/${clipperId}/block`, null, { params: { blocked } });
  return data;
}

export async function adminGetClipperProfile(clipperId) {
  const { data } = await api.get(`/admin/clippers/${clipperId}/profile`);
  return data;
}

export async function adminAdjustClipperBalance(clipperId, { amount_usd, bucket = 'pending', note = '' }) {
  const { data } = await api.post(`/admin/clippers/${clipperId}/adjust-balance`, {
    amount_usd: Number(amount_usd),
    bucket,
    note,
  });
  return data;
}

export async function adminListClips({ platform = '', clipper_id = '', flag = '', limit = 300 } = {}) {
  const { data } = await api.get('/admin/clips', { params: { platform, clipper_id, flag, limit } });
  return data;
}

export async function adminRescrapeClip(clipId) {
  const { data } = await api.post(`/admin/clips/${clipId}/rescrape`);
  return data;
}

export async function adminSetClipStatus(clipId, newStatus) {
  const { data } = await api.post(`/admin/clips/${clipId}/status`, null, { params: { new_status: newStatus } });
  return data;
}

export async function adminSetClipViews(clipId, views) {
  const { data } = await api.post(`/admin/clips/${clipId}/set-views`, { views: Number(views) });
  return data;
}

export async function adminReviewClip(clipId, decision, notes = '') {
  const { data } = await api.post(`/admin/clips/${clipId}/review`, { decision, notes });
  return data;
}

export async function adminListClipsByFlag(flag) {
  const { data } = await api.get('/admin/clips', { params: { flag } });
  return data;
}

export async function adminListClipWithdrawals(filterStatus = '', limit = 300) {
  const { data } = await api.get('/admin/clip-withdrawals', { params: { filter_status: filterStatus, limit } });
  return data;
}

export async function adminDecideClipWithdrawal(withdrawId, body) {
  const { data } = await api.post(`/admin/clip-withdrawals/${withdrawId}/decision`, body);
  return data;
}

// ===================== STAFF WORKFLOW =====================

// Separate axios instance for staff auth so it doesn't collide with admin/clipper tokens.
const staffApi = axios.create({ baseURL: API, timeout: 30000 });

const savedStaffToken =
  typeof window !== 'undefined' ? localStorage.getItem('alveefx_staff_token') : null;
if (savedStaffToken) {
  staffApi.defaults.headers.common['Authorization'] = `Bearer ${savedStaffToken}`;
}

export function setStaffToken(token) {
  if (token) {
    staffApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('alveefx_staff_token', token);
  } else {
    delete staffApi.defaults.headers.common['Authorization'];
    localStorage.removeItem('alveefx_staff_token');
  }
}

export function getStaffToken() {
  return typeof window !== 'undefined' ? localStorage.getItem('alveefx_staff_token') : null;
}

// ----- Staff self endpoints -----
export async function staffLogin(email, password) {
  const { data } = await staffApi.post('/staff/login', { email, password });
  return data;
}
export async function staffMe() {
  const { data } = await staffApi.get('/staff/me');
  return data;
}
export async function staffSubmitLog(payload) {
  // payload = { date?, metrics: {...}, note }
  const { data } = await staffApi.post('/staff/logs', payload);
  return data;
}
export async function staffListLogs() {
  const { data } = await staffApi.get('/staff/logs');
  return data;
}
export async function staffGetScores() {
  const { data } = await staffApi.get('/staff/scores');
  return data;
}

// ----- Admin: Staff CRUD + workflow -----
export async function adminListStaff() {
  const { data } = await api.get('/admin/staff');
  return data;
}
export async function adminCreateStaff(payload) {
  const { data } = await api.post('/admin/staff', payload);
  return data;
}
export async function adminUpdateStaff(id, payload) {
  const { data } = await api.put(`/admin/staff/${id}`, payload);
  return data;
}
export async function adminDeleteStaff(id) {
  const { data } = await api.delete(`/admin/staff/${id}`);
  return data;
}
export async function adminResetStaffPassword(id, new_password) {
  const { data } = await api.post(`/admin/staff/${id}/reset-password`, { new_password });
  return data;
}
export async function adminGetStaffProfile(id) {
  const { data } = await api.get(`/admin/staff/${id}/profile`);
  return data;
}
export async function adminRateStaffLog(logId, rating) {
  const { data } = await api.post(`/admin/staff/logs/${logId}/rate`, { rating });
  return data;
}
export async function adminRecomputeStaffScore(id, month) {
  const params = month ? { month } : {};
  const { data } = await api.post(`/admin/staff/${id}/recompute`, null, { params });
  return data;
}
export async function adminGetStaffScores(id) {
  const { data } = await api.get(`/admin/staff/${id}/scores`);
  return data;
}

// ----- Staff Deposit Claims (Telegram support only) -----
export async function staffSubmitDepositClaim(payload) {
  const { data } = await staffApi.post('/staff/deposit-claims', payload);
  return data;
}
export async function staffListDepositClaims() {
  const { data } = await staffApi.get('/staff/deposit-claims');
  return data;
}
export async function adminListDepositClaims(params = {}) {
  const { data } = await api.get('/admin/deposit-claims', { params });
  return data;
}
export async function adminMarkDepositClaim(id, body) {
  const { data } = await api.post(`/admin/deposit-claims/${id}/mark`, body);
  return data;
}

// ----- Attendance / Late Deduction -----
export async function staffAttendanceToday() {
  const { data } = await staffApi.get('/staff/attendance/today');
  return data;
}
export async function staffStartShift() {
  const { data } = await staffApi.post('/staff/attendance/start');
  return data;
}
export async function staffListAttendance() {
  const { data } = await staffApi.get('/staff/attendance');
  return data;
}
export async function adminGetStaffAttendance(id, month) {
  const params = month ? { month } : {};
  const { data } = await api.get(`/admin/staff/${id}/attendance`, { params });
  return data;
}

// ----- Video Uploader (video_uploader role) -----
export async function staffSubmitVideoUpload(payload) {
  // payload = { slot, tiktok_url, facebook_url?, instagram_url?, youtube_url?, note? }
  const { data } = await staffApi.post('/staff/video-uploads', payload);
  return data;
}
export async function staffListVideoUploads() {
  const { data } = await staffApi.get('/staff/video-uploads');
  return data;
}
export async function adminListStaffVideoUploads(id, month) {
  const params = month ? { month } : {};
  const { data } = await api.get(`/admin/staff/${id}/video-uploads`, { params });
  return data;
}

// ----- Market Analyst / Signal Provider -----
export async function staffSubmitSignalDelivery(payload) {
  // { trader_id?, telegram_username, status, inactive_reason?, live_call_deposit_converted?, note? }
  const { data } = await staffApi.post('/staff/signal-deliveries', payload);
  return data;
}
export async function staffListSignalDeliveries() {
  const { data } = await staffApi.get('/staff/signal-deliveries');
  return data;
}
export async function staffPatchSignalDelivery(id, payload) {
  const { data } = await staffApi.patch(`/staff/signal-deliveries/${id}`, payload);
  return data;
}
export async function staffListSignalCommissions(status_filter) {
  const params = status_filter ? { status_filter } : {};
  const { data } = await staffApi.get('/staff/signal-commissions', { params });
  return data;
}
export async function staffApproveSignalCommission(id, approve = true, note = '') {
  const { data } = await staffApi.post(`/staff/signal-commissions/${id}/approve`, { approve, note });
  return data;
}
export async function staffListSignalProviders() {
  const { data } = await staffApi.get('/staff/signal-providers');
  return data;
}
export async function adminListStaffSignalDeliveries(id, month) {
  const params = month ? { month } : {};
  const { data } = await api.get(`/admin/staff/${id}/signal-deliveries`, { params });
  return data;
}
export async function adminListStaffSignalCommissions(id, month) {
  const params = month ? { month } : {};
  const { data } = await api.get(`/admin/staff/${id}/signal-commissions`, { params });
  return data;
}

export async function staffEditDepositClaim(id, payload) {
  const { data } = await staffApi.patch(`/staff/deposit-claims/${id}`, payload);
  return data;
}

// ----- AI Staff Supervisor (WhatsApp + Claude) -----
export async function staffUpdateWhatsapp(whatsapp_number) {
  const { data } = await staffApi.put('/staff/whatsapp', { whatsapp_number });
  return data;
}
export async function staffListReminders() {
  const { data } = await staffApi.get('/staff/reminders');
  return data;
}
export async function staffAckReminder(id, action = 'done', note = '') {
  const { data } = await staffApi.post(`/staff/reminders/${id}/ack`, { action, note });
  return data;
}
export async function adminUpsertStaffBrief(id, brief_text, date_bdt) {
  const payload = { brief_text };
  if (date_bdt) payload.date_bdt = date_bdt;
  const { data } = await api.post(`/admin/staff/${id}/brief`, payload);
  return data;
}
export async function adminGetStaffBrief(id, date_bdt) {
  const params = date_bdt ? { date_bdt } : {};
  const { data } = await api.get(`/admin/staff/${id}/brief`, { params });
  return data;
}
export async function adminListStaffReminders(id, date_bdt) {
  const params = date_bdt ? { date_bdt } : {};
  const { data } = await api.get(`/admin/staff/${id}/reminders`, { params });
  return data;
}
export async function adminForceSendReminder(id) {
  const { data } = await api.post(`/admin/staff/${id}/reminder/send-now`);
  return data;
}
export async function adminGetStaffInactivity() {
  const { data } = await api.get('/admin/staff-inactivity');
  return data;
}

// ----- Personal Call Signal (public form + staff queue) -----
export async function submitPersonalCallRequest(payload) {
  // payload: { quotex_trader_id, telegram_username, note? }
  const { data } = await api.post('/personal-call/request', payload);
  return data;
}
export async function checkPersonalCallStatus(traderId) {
  const { data } = await api.get(`/personal-call/status/${encodeURIComponent(traderId)}`);
  return data;
}
export async function staffListPersonalCallRequests(status_filter) {
  const params = status_filter ? { status_filter } : {};
  const { data } = await staffApi.get('/staff/personal-call-requests', { params });
  return data;
}
export async function staffClaimPersonalCall(id, note = '') {
  const { data } = await staffApi.post(`/staff/personal-call-requests/${id}/claim`, { note });
  return data;
}
export async function staffCompletePersonalCall(id, note = '') {
  const { data } = await staffApi.post(`/staff/personal-call-requests/${id}/complete`, { note });
  return data;
}
